import React, { useEffect, useState } from 'react';
import { FileText, Lock, Pencil, Send } from 'lucide-react';
import ProfileEditForm from './ProfileEditForm';
import DocManager from './DocManager';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

/** "My application" — students view + edit their own application until it is
 *  sent to college; after that a read-only view of exactly what was sent. */
export default function StudentApplicationSection({ profile, onProfileUpdate }) {
  const [editing, setEditing] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const locked = !profile?.editable;

  useEffect(() => {
    if (!locked) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/student/me/submissions`, { credentials: 'include' });
        if (res.ok) setSubmissions((await res.json()).submissions || []);
      } catch { /* non-critical */ }
    })();
  }, [locked]);

  if (!profile) return null;

  const saveProfile = async (payload) => {
    const res = await fetch(`${API}/api/student/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, error: d.detail || 'Could not save changes.' };
    }
    onProfileUpdate(await res.json());
    setEditing(false);
    return { ok: true };
  };

  return (
    <section className="bg-white rounded-2xl border border-tge-navy/5 p-6" data-testid="student-application-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif-display text-xl text-tge-navy flex items-center gap-2">
          <FileText size={18} className="text-tge-gold" /> My application
        </h3>
        {locked ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-tge-navy/50 bg-tge-cream-soft px-3 py-1.5 rounded-full" data-testid="student-app-locked-badge">
            <Lock size={11} /> Sent to college — read only
          </span>
        ) : !editing ? (
          <button
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-tge-navy/20 text-tge-navy rounded-full px-4 py-2 hover:bg-tge-cream-soft transition"
            data-testid="student-app-edit-btn"
          >
            <Pencil size={12} /> Edit application
          </button>
        ) : null}
      </div>

      {editing ? (
        <ProfileEditForm
          initial={profile}
          onSave={saveProfile}
          onCancel={() => setEditing(false)}
          testIdPrefix="student-app-edit"
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm" data-testid="student-app-details">
          {[
            ['Full name', profile.student_name],
            ['Email', profile.email],
            ['Phone', profile.phone],
            ['Date of birth', profile.dob],
            ['Marital status', profile.marital_status],
            ['Field of study', profile.field_of_study],
            ['Preferred location', profile.preferred_location],
            ['Assigned college', profile.assigned_college_name],
            ['Assigned program', profile.assigned_program],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] uppercase tracking-wider text-tge-muted font-semibold">{label}</div>
              <div className="text-tge-navy font-medium mt-0.5">{value || '—'}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <h4 className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold mb-2">My documents</h4>
        <DocManager
          documents={profile.documents}
          canUpload={!locked}
          buildViewUrl={(k) => `${API}/api/student/me/documents/${k}`}
          buildUploadUrl={(k) => `${API}/api/student/me/documents/${k}`}
          onChanged={async () => {
            const res = await fetch(`${API}/api/student/me`, { credentials: 'include' });
            if (res.ok) onProfileUpdate(await res.json());
          }}
          testIdPrefix="student-docs"
        />
      </div>

      {locked && submissions.length > 0 && (
        <div className="mt-6 bg-tge-cream-soft/40 border border-tge-navy/10 rounded-xl p-4" data-testid="student-submissions-section">
          <h4 className="text-[11px] uppercase tracking-wider text-tge-navy font-semibold mb-2 flex items-center gap-1.5">
            <Send size={12} className="text-tge-gold" /> What was sent to the college
          </h4>
          <div className="space-y-3">
            {submissions.map((sub) => (
              <div key={sub.submission_id} className="bg-white border border-tge-navy/10 rounded-lg p-3 text-xs" data-testid={`student-submission-${sub.submission_id}`}>
                <div className="font-semibold text-tge-navy">
                  {sub.college_name || 'College'}{sub.program_name ? ` — ${sub.program_name}` : ''}
                </div>
                <div className="text-tge-navy/50 mt-0.5">
                  Sent {sub.submitted_at ? fmtDateTime(sub.submitted_at) : '—'}
                  {sub.submitted_by?.name ? ` by ${sub.submitted_by.name} (TGE)` : ''}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(sub.files_included || []).map((f) => {
                    const key = f.replace(/\.[^.]+$/, '');
                    const isProfilePdf = f === 'student_profile.pdf';
                    return isProfilePdf ? (
                      <span key={f} className="text-[10px] font-semibold bg-tge-navy/5 text-tge-navy/70 px-2 py-0.5 rounded-full">
                        📄 TGE profile summary (PDF)
                      </span>
                    ) : (
                      <button
                        key={f}
                        onClick={async () => {
                          const res = await fetch(`${API}/api/student/me/documents/${key}`, { credentials: 'include' });
                          if (!res.ok) return;
                          const blob = await res.blob();
                          window.open(URL.createObjectURL(blob), '_blank');
                        }}
                        className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full hover:bg-emerald-100 transition"
                        data-testid={`student-sent-doc-${key}`}
                      >
                        ✓ {key.replace(/_/g, ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
